import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Dictionary } from "@insite/client-framework/Common/Types";
import printWithImagesLoaded from "@tSurya/Common/Utilities/printWithImagesLoaded";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setShareListModalIsOpen from "@insite/client-framework/Store/Components/ShareListModal/Handlers/SetShareListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    canAddWishListLineToCart,
    getWishListLinesDataView,
} from "@insite/client-framework/Store/Data/WishListLines/WishListLinesSelectors";
import updateWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/UpdateWishList";
import { getWishListState } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import addLinesToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddLinesToCart";
import addWishListToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddWishListToCart";
import deleteWishList from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/DeleteWishList";
import deleteWishListLines from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/DeleteWishListLines";
import exportWishList from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/ExportWishList";
import loadWishListIfNeeded from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/LoadWishListIfNeeded";
import loadWishListLines from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/LoadWishListLines";
import loadWishLists from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/LoadWishLists";
import setAllWishListLinesIsSelected from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/SetAllWishListLinesIsSelected";
import setWishListTags from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/SetWishListTags";
import updateLoadWishListLinesParameter from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateLoadWishListLinesParameter";
import translate from "@insite/client-framework/Translate";
import {
    CartLineCollectionModel,
    CartLineModel,
    WishListLineModel,
    WishListModel,
} from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ScheduleReminderModal, {
    ScheduleReminderModalStyles,
} from "@insite/content-library/Components/ScheduleReminderModal";
import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import WishListPrintModal, { WishListPrintModalStyles } from "@insite/content-library/Components/WishListPrintModal";
import MyListsEditListForm from "@insite/content-library/Widgets/MyLists/MyListsEditListForm";
import AddTagsModal from "@insite/content-library/Widgets/MyListsDetails/AddTagsModal";
import MyListsDetailsCopyListForm from "@insite/content-library/Widgets/MyListsDetails/MyListsDetailsCopyListForm";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Star from "@insite/mobius/Icons/Star";
import StarX from "@insite/mobius/Icons/StarX";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import OverflowMenu, { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import { markForFocusLater } from "@insite/mobius/Overlay/helpers/focusManager";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import PrintAllPagesModal from "@tSurya/Components/PrintallPage";
import PrintIncludePricesModal from "@tSurya/Components/PrintIncludePricesModal";
import PrintProjectView from "@tSurya/Components/PrintProjectView";
import { tryParseSelections } from "@tSurya/Common/MgbwConfiguratorHelpers";
import { MGBW_SELECTIONS, MGBW_SELECTIONS_INPUT } from "@tSurya/Common/MgbwPropertyKeys";
import { buildConfiguratorLineInput } from "@tSurya/Common/MgbwConfiguratorHelpers";
import { COLORS } from "@tSurya/theme/design";
import { isThemeActive, THEME_IDS } from "@tSurya/theme/themeHelper";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

type WishListLineWithProperties = WishListLineModel & {
    properties?: Record<string, string>;
};

interface State {
    updateListModalIsOpen: boolean;
    deleteListModalIsOpen: boolean;
    deleteListItemsModalIsOpen: boolean;
    copyListModalIsOpen: boolean;
    printAllModalIsOpen: boolean;
    includePricesModalIsOpen: boolean;
    printIncludePrices: boolean;
    addTagsModalIsOpen: boolean;
    scheduleReminderModalIsOpen: boolean;
    closeCopyListModalOnEsc: boolean;
    totalListItem: number;
}

const mapStateToProps = (state: ApplicationState) => ({
    wishList: getWishListState(state, state.pages.myListDetails.wishListId).value,
    wishListLinesDataView: getWishListLinesDataView(state, state.pages.myListDetails.loadWishListLinesParameter),
    wishListSettings: getSettingsCollection(state).wishListSettings,
    selectedWishListLineIds: state.pages.myListDetails.selectedWishListLineIds,
    myListsPageLink: getPageLinkByPageType(state, "MyListsPage"),
    allowEditingOfWishLists: getSettingsCollection(state).wishListSettings.allowEditingOfWishLists,
    productInfosByWishListLineId: state.pages.myListDetails.productInfosByWishListLineId,
    isUpdatingCart: state.context.isUpdatingCart,
    isLoading: !!(
        state.pages.myListDetails.wishListId && state.data.wishLists.isLoading[state.pages.myListDetails.wishListId]
    ),
});

const mapDispatchToProps = {
    addWishListToCart,
    addLinesToCart,
    deleteWishList,
    deleteWishListLines,
    loadWishLists,
    loadWishListLines: makeHandlerChainAwaitable(loadWishListLines),
    updateLoadWishListLinesParameter,
    setShareListModalIsOpen,
    setAllWishListLinesIsSelected,
    exportWishList,
    updateWishList,
    loadWishListIfNeeded,
    setWishListTags,
};

const enum fields {
    copyButton = "copyButton",
    exportButton = "exportButton",
    printButton = "printButton",
    deleteListButton = "deleteListButton",
    removeSelectedButton = "removeSelectedButton",
    addTagsButton = "addTagsButton",
    editReminderButton = "editReminderButton",
    editListButton = "editListButton",
    addListToCartButton = "addListToCartButton",
    hideButton = "hideButton",
    shareButton = "shareButton",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.copyButton]: boolean;
        [fields.exportButton]: boolean;
        [fields.printButton]: boolean;
        [fields.deleteListButton]: boolean;
        [fields.removeSelectedButton]: boolean;
        [fields.addTagsButton]: boolean;
        [fields.editReminderButton]: boolean;
        [fields.editListButton]: boolean;
        [fields.addListToCartButton]: boolean;
        [fields.hideButton]: boolean;
        [fields.shareButton]: boolean;
    };
}

type Props = OwnProps & HasHistory & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface MyListsDetailsActionStyles {
    wrapper?: InjectableCss;
    buttonWrapper?: InjectableCss;
    wideHidden?: HiddenProps;
    nameText?: TypographyProps;
    overflowMenu?: OverflowMenuPresentationProps;
    narrowHidden?: HiddenProps;
    printButton?: ButtonPresentationProps;
    addTagsButton?: ButtonPresentationProps;
    scheduleButton?: ButtonPresentationProps;
    editButton?: ButtonPresentationProps;
    shareButton?: ButtonPresentationProps;
    hideButton?: ButtonPresentationProps;
    addItemButton?: ButtonPresentationProps;
    removeSelectedButton?: ButtonPresentationProps;
    addListButton?: ButtonPresentationProps;
    editListModal?: ModalPresentationProps;
    deleteListModal?: TwoButtonModalStyles;
    deleteListItemsModal?: TwoButtonModalStyles;
    printListModal?: TwoButtonModalStyles;
    copyListModal?: ModalPresentationProps;
    scheduleReminderModal?: ScheduleReminderModalStyles;
    listOptions?: InjectableCss;
    iconContainer?: InjectableCss;
    actionFavoriteClickable?: ClickablePresentationProps;
    starIcon?: IconPresentationProps;
    starXIcon?: IconPresentationProps;
    copyButton?: ButtonPresentationProps;
    exportButton?: ButtonPresentationProps;
    deleteButton?: ButtonPresentationProps;
    wishtListnameAndFavoriteIcon: InjectableCss;
}

// On MGBW / Global Views the brand theme bakes a fill-on-hover into the button
// defaultProps (secondary -> primary background, primary -> light background). On the
// Project Details page we mirror Surya, where buttons stay static on hover. These helpers
// re-pin the hover state back to each button's resting look. They are gated to MGBW / GV so
// Surya keeps its own hover behaviour, and use a function interpolation so the brand check is
// evaluated per render (SSR-safe).
const secondaryHoverReset = () =>
    isThemeActive(THEME_IDS.MGBW) || isThemeActive(THEME_IDS.GLOBAL_VIEWS)
        ? css`
              &:hover {
                  background-color: ${getColor("primary.contrast")} !important;
                  border-color: ${getColor("primary")} !important;
                  color: ${getColor("text.main")} !important;
                  opacity: 1 !important;
                  box-shadow: none !important;
                  span[class^="TypographyStyle"] {
                      color: ${getColor("text.main")} !important;
                  }
              }
          `
        : css``;

const primaryHoverReset = () =>
    isThemeActive(THEME_IDS.MGBW) || isThemeActive(THEME_IDS.GLOBAL_VIEWS)
        ? css`
              &:hover {
                  background-color: ${getColor("primary")} !important;
                  border-color: ${getColor("primary")} !important;
                  color: ${getColor("primary.contrast")} !important;
                  opacity: 1 !important;
                  box-shadow: none !important;
                  span[class^="TypographyStyle"] {
                      color: ${getColor("primary.contrast")} !important;
                  }
              }
          `
        : css``;

// Forces the text colour of white (secondary) buttons on the Project Details page
// to #545454 on MGBW / Global Views so it matches the Surya visual reference.
// Returns empty on Surya so existing Surya styling is untouched.
const secondaryTextColor = () =>
    isThemeActive(THEME_IDS.MGBW) || isThemeActive(THEME_IDS.GLOBAL_VIEWS)
        ? css`
              color: #545454;
              span[class^="TypographyStyle"] {
                  color: #545454;
              }
          `
        : css``;

export const actionStyles: MyListsDetailsActionStyles = {
    wishtListnameAndFavoriteIcon: {
        css: css`
            display: flex;
            justify-content: flex-start;
            gap: 20px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 85%;
                        max-width: 85%;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
        `,
    },
    buttonWrapper: {
        css: css`
            display: flex;
            flex-wrap: nowrap;
            align-items: flex-start;
        `,
    },
    wideHidden: {
        below: "lg",
        css: css`
            display: flex;
            flex-wrap: nowrap;
        `,
    },
    nameText: {
        variant: "h3",
        as: "h1",
        ellipsis: true,
    },
    narrowHidden: {
        above: "md",
    },
    printButton: {
        css: css`
            padding: 0 15px;
            margin-right: 10px;
            border: 1px solid ${getColor("primary")};
            background-color: ${getColor("primary.contrast")};
            color: ${getColor("text.main")};
            ${secondaryTextColor}
            :hover {
                opacity: 0.3;
                background-color: ${getColor("primary.contrast")} !important;
                border-color: ${getColor("text.main")} !important;
                color: ${getColor("text.main")} !important;
                span[class^="TypographyStyle"] {
                    color: ${getColor("text.main")} !important;
                }
            }
        `,
        variant: "secondary",
        hoverMode: "lighten",
    },
    addTagsButton: {
        css: css`
            padding: 0 10px;
            margin-right: 10px;
            white-space: nowrap;
            ${secondaryTextColor}
            ${secondaryHoverReset}
        `,
        variant: "secondary",
    },
    scheduleButton: {
        css: css`
            padding: 0 15px;
            margin-right: 10px;
            border: 1px solid ${getColor("primary")};
            background-color: ${getColor("primary.contrast")};
            color: ${getColor("text.main")};
            ${secondaryTextColor}
            :hover {
                opacity: 0.3;
                background-color: ${getColor("primary.contrast")} !important;
                border-color: ${getColor("text.main")} !important;
                color: ${getColor("text.main")} !important;
                span[class^="TypographyStyle"] {
                    color: ${getColor("text.main")} !important;
                }
            }
        `,
        variant: "secondary",
        hoverMode: "lighten",
    },
    editButton: {
        css: css`
            padding: 0 15px;
            margin-right: 10px;
            border: 1px solid ${getColor("primary")};
            background-color: ${getColor("primary.contrast")};
            color: ${getColor("text.main")};
            ${secondaryTextColor}
            :hover {
                opacity: 0.3;
                background-color: ${getColor("primary.contrast")} !important;
                border-color: ${getColor("text.main")} !important;
                color: ${getColor("text.main")} !important;
                span[class^="TypographyStyle"] {
                    color: ${getColor("text.main")} !important;
                }
            }
        `,
        variant: "secondary",
        hoverMode: "lighten",
    },
    shareButton: {
        css: css`
            padding: 0 15px;
            margin-right: 10px;
            border: 1px solid ${getColor("primary")};
            background-color: ${getColor("primary.contrast")};
            color: ${getColor("text.main")};
            ${secondaryTextColor}
            ${secondaryHoverReset}
        `,
        variant: "secondary",
    },
    addItemButton: {
        typographyProps: {
            weight: "bold",
            css: css`
                white-space: nowrap;
            `,
        },
        css: css`
            padding: 0 15px;
            margin-right: 10px;
            ${secondaryTextColor}
            ${secondaryHoverReset}
        `,
        variant: "secondary",
    },
    removeSelectedButton: {
        typographyProps: {
            weight: "bold",
            css: css`
                white-space: nowrap;
            `,
        },
        css: css`
            padding: 0 15px;
            margin-right: 10px;
        `,
        variant: "tertiary",
    },
    addListButton: {
        typographyProps: {
            weight: "normal",
            css: css`
                white-space: nowrap;
                ${isThemeActive(THEME_IDS.MGBW) || isThemeActive(THEME_IDS.GLOBAL_VIEWS)
                    ? "font-style: italic;"
                    : ""}
            `,
        },
        css: css`
            padding: 0 15px;
            ${primaryHoverReset}
            &:hover {
                /* background: ${getColor("text.accent")}; */
                span[class^="TypographyStyle"] {
                    /* color: ${getColor("secondary")}; */
                }
            }
        `,
    },
    editListModal: {
        sizeVariant: "small",
    },
    copyListModal: {
        sizeVariant: "small",
        cssOverrides: {
            modalBody: css`
                overflow: visible;
            `,
        },
    },
    listOptions: {
        css: css`
            &:active {
                background-color: #ffffff !important;
                background: #ffffff !important;
            }
            &:focus {
                color: ${getColor("text.main")} !important;
            }
            &:hover {
                background-color: #ffffff !important;
                background: #ffffff !important;
            }
            button &:active {
                background-color: #ffffff !important;
                background: #ffffff !important;
            }
        `,
    },
    iconContainer: {
        css: css`
            width: 15px;
            margin-top: 12px;
        `,
    },
    actionFavoriteClickable: {
        css: css`
            width: 15px;
        `,
    },
    starIcon: {
        src: Star,
        size: 15,
    },
    starXIcon: {
        src: StarX,
        size: 15,
    },
    copyButton: {
        css: css`
            padding: 0 15px;
            margin-right: 10px;
            white-space: nowrap;
            ${secondaryTextColor}
            ${secondaryHoverReset}
        `,
        variant: "secondary",
    },
    exportButton: {
        css: css`
            padding: 0 15px;
            margin-right: 10px;
            white-space: nowrap;
            ${secondaryTextColor}
            ${secondaryHoverReset}
        `,
        variant: "secondary",
    },
    deleteButton: {
        css: css`
            padding: 0 15px;
            margin-right: 10px;
            white-space: nowrap;
            ${secondaryTextColor}
            ${secondaryHoverReset}
        `,
        variant: "secondary",
    },
};

const styles = actionStyles;

class MyListsDetailsActions extends React.Component<Props, State> {
    static contextType = ToasterContext;
    declare context: React.ContextType<typeof ToasterContext>;

    constructor(props: Props) {
        super(props);
        this.state = {
            updateListModalIsOpen: false,
            printAllModalIsOpen: false,
            includePricesModalIsOpen: false,
            printIncludePrices: true,
            addTagsModalIsOpen: false,
            deleteListModalIsOpen: false,
            deleteListItemsModalIsOpen: false,
            copyListModalIsOpen: false,
            scheduleReminderModalIsOpen: false,
            closeCopyListModalOnEsc: true,
            totalListItem: 0,
        };
    }

    displayToast(message: React.ReactNode) {
        this.context.addToast({ body: message, messageType: "success" });
    }

    linesSelected() {
        return this.props.selectedWishListLineIds.length > 0;
    }

    enableAddToCart() {
        const lines = this.props.wishListLinesDataView.value;
        const linesToValidate = this.linesSelected()
            ? (lines?.filter(o => this.props.selectedWishListLineIds.indexOf(o.id) > -1) ?? [])
            : (lines ?? []);
        return (
            this.props.wishList?.canAddToCart &&
            !this.props.isUpdatingCart &&
            linesToValidate.length > 0 &&
            linesToValidate.every(o => canAddWishListLineToCart(o, this.props.productInfosByWishListLineId)) &&
            this.allQuantitiesAreValid(linesToValidate)
        );
    }

    allQuantitiesAreValid(wishListLines: WishListLineModel[]): boolean {
        return wishListLines
            .filter(
                o =>
                    this.props.selectedWishListLineIds.length === 0 ||
                    this.props.selectedWishListLineIds.indexOf(o.id) > -1,
            )
            .every(o => o.qtyOrdered && parseFloat(o.qtyOrdered.toString()) > 0);
    }

    editClickHandler = () => {
        this.setState({ updateListModalIsOpen: true });
    };

    shareClickHandler = () => {
        this.props.setShareListModalIsOpen({ modalIsOpen: true, wishListId: this.props.wishList?.id });
    };

    printOrOpenPrintAllModal = () => {
        const { wishListLinesDataView } = this.props;
        if (!wishListLinesDataView.value || !wishListLinesDataView.pagination) {
            return null;
        }
        this.setState({ includePricesModalIsOpen: true });
    };

    closePrintModal = () => {
        this.setState({ printAllModalIsOpen: false });
    };

    closeIncludePricesModal = () => {
        this.setState({ includePricesModalIsOpen: false });
    };

    handleIncludePricesConfirm = (includePrices: boolean) => {
        const { wishListLinesDataView } = this.props;
        if (!wishListLinesDataView.value || !wishListLinesDataView.pagination) {
            return;
        }
        this.setState({ printIncludePrices: includePrices });
        const { pagination: { totalItemCount, pageSize } } = wishListLinesDataView;
        if (pageSize < totalItemCount) {
            // Paginated list: load all items first so the dedicated print view contains every line.
            this.setState({ printAllModalIsOpen: true });
        } else {
            // All items already loaded: trigger print directly.
            setTimeout(() => printWithImagesLoaded(), 300);
        }
    };

    editCloseHandler = () => {
        this.setState({ updateListModalIsOpen: false });
    };

    addToCartClickHandler = (e: any) => {
        e.preventDefault();
        if (!this.props.wishList || !this.props.wishListLinesDataView.value) {
            return;
        }
        const changedSharedListLinesQuantities: Dictionary<number> = {};
        for (const wishListLineId in this.props.productInfosByWishListLineId) {
            changedSharedListLinesQuantities[wishListLineId] =
                this.props.productInfosByWishListLineId[wishListLineId]!.qtyOrdered;
        }
        if (this.linesSelected()) {
            const selectedLines = this.props.wishListLinesDataView.value.filter(
                o => this.props.selectedWishListLineIds.indexOf(o.id) > -1,
            );
            const linesToAdd = selectedLines.filter(
                o =>
                    o.quoteRequired ||
                    o.allowZeroPricing ||
                    this.props.productInfosByWishListLineId[o.id]?.pricing?.unitNetPrice !== 0,
            );

            if (linesToAdd.length === 0) {
                this.context.addToast({ body: siteMessage("Cart_InvalidPrice"), messageType: "danger" });
                return;
            }

            this.props.addLinesToCart({
                apiParameter: {
                    cartId: API_URL_CURRENT_FRAGMENT,
                    cartLineCollection: {
                        cartLines: linesToAdd.map(o => {
                            const line = o as WishListLineWithProperties;
                            const properties = buildConfiguratorLineInput(line.properties);

                            return {
                                productId: o.productId,
                                qtyOrdered: changedSharedListLinesQuantities[o.id],
                                unitOfMeasure: o.unitOfMeasure,
                                notes: o.notes,
                                ...(Object.keys(properties).length > 0 ? { properties } : {}),
                            } as any as CartLineModel;
                        }),
                    } as CartLineCollectionModel,
                },
                onSuccess: () => {
                    this.onAddToCartSuccess(
                        {
                            notAllAddedToCart: linesToAdd.length !== selectedLines.length,
                        } as CartLineCollectionModel,
                        linesToAdd.length,
                        selectedLines.length,
                    );
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        this.onSuccess?.();
                    }
                },
            });
        } else {
            this.setState({
                ...this.state,
                totalListItem: Object.keys(changedSharedListLinesQuantities).length,
            });

            this.props.addWishListToCart({
                apiParameter: {
                    wishListId: this.props.wishList.id,
                    changedSharedListLinesQuantities,
                },
                onSuccess: this.onAddToCartSuccess,
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        this.onSuccess?.(resultProps.apiResult);
                    }
                },
            });
        }
    };

    onAddToCartSuccess = (
        apiResult?: CartLineCollectionModel,
        addedItemCount = apiResult?.cartLines?.length ?? 0,
        totalItemCount = this.state.totalListItem,
    ) => {
        this.displayToast(
            apiResult?.notAllAddedToCart
                ? siteMessage("Lists_Items_Not_All_Added_To_Cart")
                : `${siteMessage("Cart_AllProductsAddedToCart")} ${addedItemCount} added to cart out of ${totalItemCount}`,
        );
    };

    removeSelectedClickHandler = () => {
        this.setState({ deleteListItemsModalIsOpen: true });
    };

    removeSelectedCancelHandler = () => {
        this.setState({ deleteListItemsModalIsOpen: false });
    };

    removeSelectedSubmitHandler = () => {
        if (!this.props.wishList || !this.props.wishListLinesDataView.value) {
            return;
        }

        this.setState({ deleteListItemsModalIsOpen: false });
        const wishListLineIds = this.props.wishListLinesDataView.value
            .filter(o => this.props.selectedWishListLineIds.indexOf(o.id) > -1)
            .map(o => o.id);
        this.props.deleteWishListLines({
            wishListId: this.props.wishList.id,
            wishListLineIds,
            onSuccess: this.onRemoveSelectedSuccess,
            onComplete() {
                this.onSuccess?.();
            },
        });
    };

    onRemoveSelectedSuccess = () => {
        this.displayToast(translate(`Item${this.props.selectedWishListLineIds.length > 1 ? "s" : ""} Deleted`));
        this.props.setAllWishListLinesIsSelected({ isSelected: false });
    };

    deleteClickHandler = () => {
        this.setState({ deleteListModalIsOpen: true });
    };

    deleteCancelHandler = () => {
        this.setState({ deleteListModalIsOpen: false });
    };

    deleteSubmitHandler = () => {
        if (!this.props.wishList) {
            return;
        }

        this.setState({ deleteListModalIsOpen: false });
        this.props.myListsPageLink && this.props.history.push(this.props.myListsPageLink.url);
        this.props.deleteWishList({
            wishListId: this.props.wishList.id,
            onSuccess: this.onDeleteSuccess,
            onComplete() {
                this.onSuccess?.();
            },
        });
    };

    onDeleteSuccess = () => {
        this.displayToast(translate("List Deleted"));
    };

    copyClickHandler = () => {
        this.props.loadWishLists();
        this.setState({ copyListModalIsOpen: true });
    };

    exportClickHandler = () => {
        if (!this.props.wishList) {
            return;
        }

        this.props.exportWishList({
            wishListId: this.props.wishList.id,
        });
    };

    copyCancelHandler = () => {
        this.setState({ copyListModalIsOpen: false });
    };

    copySubmitHandler = () => {
        this.setState({ copyListModalIsOpen: false });
    };

    disableCloseCopyListModalOnEsc = () => {
        this.setState({ closeCopyListModalOnEsc: false });
    };

    enableCloseCopyListModalOnEsc = () => {
        this.setState({ closeCopyListModalOnEsc: true });
    };

    scheduleReminderClickHandler = () => {
        this.setState({ scheduleReminderModalIsOpen: true });
    };

    closeScheduleReminderModalHandler = () => {
        this.setState({ scheduleReminderModalIsOpen: false });
    };

    toggleFavorite = (wishList: WishListModel) => {
        if (!wishList) {
            return;
        }

        const that = this;
        this.props.updateWishList({
            apiParameter: {
                wishList: {
                    ...wishList,
                    isFavorite: !wishList.isFavorite,
                    wishListLineCollection: null,
                },
            },
            onComplete(resultProps) {
                if (resultProps.result?.wishList) {
                    that.props.loadWishListIfNeeded({ wishListId: resultProps.result?.wishList.id });
                } else if (resultProps.result?.errorMessage) {
                    this.onError?.(resultProps.result.errorMessage);
                }
            },
        });
    };

    addTagsClickHandler = () => {
        this.setState({ addTagsModalIsOpen: true });
    };

    closeAddTagsModalHandler = () => {
        this.setState({ addTagsModalIsOpen: false });
        const tags = this.props.wishList?.wishListTags?.map(o => o.tag);
        if (tags) {
            this.props.setWishListTags({ wishListTags: tags });
        }
    };

    toggleHide = (wishList: WishListModel) => {
        if (!wishList) {
            return;
        }

        const that = this;
        this.props.updateWishList({
            apiParameter: {
                wishList: {
                    ...wishList,
                    isHidden: !wishList.isHidden,
                    wishListLineCollection: null,
                },
            },
            onComplete(resultProps) {
                if (resultProps.result?.wishList) {
                    that.props.loadWishListIfNeeded({ wishListId: resultProps.result?.wishList.id });
                } else if (resultProps.result?.errorMessage) {
                    this.onError?.(resultProps.result.errorMessage);
                }
            },
        });
    };

    render() {
        const { wishList, wishListSettings, wishListLinesDataView, allowEditingOfWishLists, isLoading } = this.props;
        if (!wishList) {
            return null;
        }
        const showEdit = !wishList.isSharedList && !wishList.isAutogenerated;
        const showShare =
            !wishList.isSharedList &&
            wishListSettings.allowMultipleWishLists &&
            wishListSettings.allowListSharing &&
            !wishList.isAutogenerated;
        const showRemoveSelected =
            (wishList.allowEdit || !wishList.isSharedList) && this.linesSelected() && !wishList.isAutogenerated;
        const showSchedule = wishListSettings.enableWishListReminders && !wishList.isAutogenerated;
        const showAddToCart = (wishListLinesDataView.value?.length || 0) > 0;
        const showCopy = wishListSettings.allowMultipleWishLists && showAddToCart;
        const addListToCartButtonText = this.linesSelected()
            ? translate("Add Selected to Cart")
            : translate("Add List to Cart");
        const scheduleButtonText = wishList.schedule ? translate("Edit Reminder") : translate("Schedule Reminder");
        const showDelete = allowEditingOfWishLists && !wishList.isSharedList && !wishList.isAutogenerated;
        const toggleHideButtonText = wishList.isHidden ? translate("Unhide List") : translate("Hide Project");
        const toggleFavoriteLabel = wishList.isFavorite
            ? translate("Remove from favorites")
            : translate("Add to favorites");
        const overflowItems = [];
        const wideHiddenOverflowMenu = [];
        const wideHiddenButtons = [];

        if (showCopy) {
            if (this.props.fields.copyButton === false) {
                wideHiddenButtons.push(
                    <Button
                        {...styles.copyButton}
                        onClick={this.copyClickHandler}
                        data-test-selector="copyList"
                        key="copy"
                    >
                        {translate("Copy")}
                    </Button>,
                );
            } else {
                overflowItems.push(
                    <Clickable onClick={this.copyClickHandler} data-test-selector="copyList" key="copy">
                        {translate("Copy")}
                    </Clickable>,
                );
            }
        }

        if (this.props.fields.printButton === false) {
            wideHiddenButtons.push(
                <Button
                    {...styles.printButton}
                    onClick={this.printOrOpenPrintAllModal}
                    key="print"
                    data-test-selector="myListsDetails_printButton"
                    disabled={(wishListLinesDataView.value?.length ?? 0) <= 0}
                >
                    {translate("Print")}
                </Button>,
            );
        } else {
            overflowItems.push(
                <Clickable
                    onClick={this.printOrOpenPrintAllModal}
                    data-test-selector="myListsDetails_printButton"
                    key="print"
                    disabled={(wishListLinesDataView.value?.length ?? 0) <= 0}
                >
                    {translate("Print")}
                </Clickable>,
            );
        }

        if (!wishList.isAutogenerated) {
            if (this.props.fields.addTagsButton) {
                overflowItems.push(
                    <Clickable onClick={this.addTagsClickHandler} data-test-selector="addTags" key="addTags">
                        {translate("Add Tags")}
                    </Clickable>,
                );
            } else {
                wideHiddenButtons.push(
                    <Button
                        {...styles.addTagsButton}
                        onClick={this.addTagsClickHandler}
                        data-test-selector="addTags"
                        key="addTags"
                    >
                        {translate("Add Tags")}
                    </Button>,
                );
            }
        }

        if (showSchedule) {
            if (this.props.fields.editReminderButton === false) {
                wideHiddenButtons.push(
                    <Button
                        {...styles.scheduleButton}
                        key="scheduleReminder"
                        onClick={this.scheduleReminderClickHandler}
                        data-test-selector="scheduleReminderList"
                    >
                        {scheduleButtonText}
                    </Button>,
                );
            } else {
                overflowItems.push(
                    <Clickable
                        onClick={this.scheduleReminderClickHandler}
                        data-test-selector="scheduleReminderList"
                        key="scheduleReminder"
                    >
                        {scheduleButtonText}
                    </Clickable>,
                );
            }
        }

        if (!wishList.isAutogenerated) {
            if (this.props.fields.hideButton === false) {
                wideHiddenButtons.push(
                    <Button
                        {...styles.hideButton}
                        key="hide"
                        onClick={() => this.toggleHide(wishList)}
                        data-test-selector="myListsDetailsHide"
                        disabled={isLoading}
                    >
                        {toggleHideButtonText}
                    </Button>,
                );
            } else {
                overflowItems.push(
                    <Clickable
                        key="hide"
                        onClick={() => this.toggleHide(wishList)}
                        data-test-selector="myListsDetailsHide"
                        disabled={isLoading}
                    >
                        {toggleHideButtonText}
                    </Clickable>,
                );
            }
        }

        if (showEdit) {
            if (this.props.fields.editListButton === false) {
                wideHiddenButtons.push(
                    <Button
                        {...styles.editButton}
                        onClick={this.editClickHandler}
                        data-test-selector="editList"
                        key="edit"
                    >
                        {translate("Edit")}
                    </Button>,
                );
            } else {
                overflowItems.push(
                    <Clickable onClick={this.editClickHandler} data-test-selector="editList" key="edit">
                        {translate("Edit")}
                    </Clickable>,
                );
            }
        }

        if (showShare) {
            if (this.props.fields.shareButton) {
                overflowItems.push(
                    <Clickable
                        onClick={this.shareClickHandler}
                        data-test-selector="myListDetails_shareList"
                        key="share"
                    >
                        {translate("Share")}
                    </Clickable>,
                );
            } else {
                wideHiddenButtons.push(
                    <Button
                        {...styles.shareButton}
                        key="share"
                        onClick={this.shareClickHandler}
                        data-test-selector="myListDetails_shareList"
                    >
                        {translate("Share")}
                    </Button>,
                );
            }
        }

        if (showRemoveSelected) {
            if (this.props.fields.removeSelectedButton === false) {
                wideHiddenButtons.push(
                    <Button
                        {...styles.removeSelectedButton}
                        key="removeSelected"
                        onClick={this.removeSelectedClickHandler}
                        data-test-selector="myListDetails_removeSelected"
                    >
                        {translate("Remove Selected")}
                    </Button>,
                );
            } else {
                overflowItems.push(
                    <Clickable
                        onClick={this.removeSelectedClickHandler}
                        key="removeSelected"
                        data-test-selector="myListDetails_removeSelected"
                    >
                        {translate("Remove Selected")}
                    </Clickable>,
                );
            }
        }

        if (this.props.fields.addListToCartButton) {
            overflowItems.push(
                <Clickable
                    key="addToCart"
                    onClick={this.addToCartClickHandler}
                    data-test-selector="myListDetails_addListToCart"
                >
                    {addListToCartButtonText}
                </Clickable>,
            );
        } else {
            wideHiddenButtons.push(
                <Button
                    key="addToCart"
                    {...styles.addListButton}
                    onClick={this.addToCartClickHandler}
                    data-test-selector="myListDetails_addListToCart"
                >
                    {addListToCartButtonText}
                </Button>,
            );
        }

        if (wishList.wishListLinesCount > 0) {
            if (this.props.fields.exportButton === false) {
                wideHiddenButtons.push(
                    <Button
                        {...styles.exportButton}
                        onClick={this.exportClickHandler}
                        data-test-selector="exportList"
                        key="export"
                    >
                        {translate("Export")}
                    </Button>,
                );
            } else {
                overflowItems.push(
                    <Clickable onClick={this.exportClickHandler} data-test-selector="exportList" key="export">
                        {translate("Export")}
                    </Clickable>,
                );
            }
        }

        if (showDelete) {
            if (this.props.fields.deleteListButton === false) {
                wideHiddenButtons.push(
                    <Button
                        {...styles.deleteButton}
                        onClick={this.deleteClickHandler}
                        data-test-selector="deleteList"
                        key="delete"
                    >
                        {translate("Delete")}
                    </Button>,
                );
            } else {
                overflowItems.push(
                    <Clickable onClick={this.deleteClickHandler} data-test-selector="deleteList" key="delete">
                        {translate("Delete")}
                    </Clickable>,
                );
            }
        }

        if (overflowItems.length > 0) {
            wideHiddenOverflowMenu.push(
                <OverflowMenu key="overflowMenu" onOpen={markForFocusLater} {...styles.overflowMenu}>
                    {overflowItems}
                </OverflowMenu>,
            );
        }

        return (
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.wishtListnameAndFavoriteIcon}>
                    {!wishList.isAutogenerated && (
                        <StyledWrapper {...styles.iconContainer}>
                            <Clickable
                                {...styles.actionFavoriteClickable}
                                onClick={() => this.toggleFavorite(wishList)}
                                aria-label={toggleFavoriteLabel}
                                data-test-selector="myListsDetailsFavorite"
                                disabled={isLoading}
                            >
                                {wishList.isFavorite && (
                                    <Icon {...styles.starXIcon} data-test-selector="myListsDetailsFavoriteStarXIcon" />
                                )}
                                {!wishList.isFavorite && (
                                    <Icon {...styles.starIcon} data-test-selector="myListsDetailsFavoriteStarIcon" />
                                )}
                            </Clickable>
                        </StyledWrapper>
                    )}
                    <Typography {...styles.nameText} data-test-selector="listName">
                        {wishList.name}
                    </Typography>
                </StyledWrapper>

                <StyledWrapper {...styles.buttonWrapper} data-test-selector="menuWrapper">
                    <Hidden {...styles.narrowHidden}>
                        <OverflowMenu position="end" onOpen={markForFocusLater} {...styles.overflowMenu}>
                            <Clickable onClick={this.addToCartClickHandler}>{addListToCartButtonText}</Clickable>
                            {showRemoveSelected ? (
                                <Clickable onClick={this.removeSelectedClickHandler}>
                                    {translate("Remove Selected")}
                                </Clickable>
                            ) : null}
                            {showShare ? (
                                <Clickable onClick={this.shareClickHandler}>{translate("Share")}</Clickable>
                            ) : null}
                            {showEdit ? (
                                <Clickable onClick={this.editClickHandler}>{translate("Edit")} </Clickable>
                            ) : null}
                            {!wishList.isAutogenerated && (
                                <Clickable onClick={() => this.toggleHide(wishList)} disabled={isLoading}>
                                    {toggleHideButtonText}
                                </Clickable>
                            )}
                            {showSchedule ? (
                                <Clickable onClick={this.scheduleReminderClickHandler}>{scheduleButtonText}</Clickable>
                            ) : null}
                            {!wishList.isAutogenerated && (
                                <Clickable onClick={this.addTagsClickHandler}>{translate("Add Tags")}</Clickable>
                            )}
                            <Clickable onClick={this.printOrOpenPrintAllModal}>{translate("Print")}</Clickable>
                            {showCopy ? (
                                <Clickable onClick={this.copyClickHandler}>{translate("Copy")}</Clickable>
                            ) : null}
                            {wishList.wishListLinesCount > 0 ? (
                                <Clickable onClick={this.exportClickHandler}>{translate("Export")}</Clickable>
                            ) : null}
                            {showDelete ? (
                                <Clickable onClick={this.deleteClickHandler}>{translate("Delete")}</Clickable>
                            ) : null}
                        </OverflowMenu>
                    </Hidden>
                    <Hidden {...styles.wideHidden} data-test-selector="wideHidden">
                        {wideHiddenButtons}
                        {wideHiddenOverflowMenu}
                    </Hidden>
                </StyledWrapper>
                <Modal
                    headline={translate("Edit Project Detail")}
                    {...styles.editListModal}
                    isOpen={this.state.updateListModalIsOpen}
                    handleClose={this.editCloseHandler}
                >
                    <MyListsEditListForm
                        wishList={wishList}
                        onCancel={this.editCloseHandler}
                        onSubmit={this.editCloseHandler}
                    ></MyListsEditListForm>
                </Modal>
                <TwoButtonModal
                    headlineText={translate("Delete List")}
                    {...styles.deleteListModal}
                    modalIsOpen={this.state.deleteListModalIsOpen}
                    messageText={`${translate("Are you sure you want to delete")} ${wishList.name}?`}
                    cancelButtonText={translate("Cancel")}
                    submitButtonText={translate("Delete")}
                    onCancel={this.deleteCancelHandler}
                    onSubmit={this.deleteSubmitHandler}
                    submitTestSelector="submitDeleteList"
                ></TwoButtonModal>
                <TwoButtonModal
                    headlineText={translate(`Delete Item${this.props.selectedWishListLineIds.length > 1 ? "s" : ""}`)}
                    {...styles.deleteListItemsModal}
                    modalIsOpen={this.state.deleteListItemsModalIsOpen}
                    messageText={translate(
                        `Are you sure you want to delete ${
                            this.props.selectedWishListLineIds.length > 1 ? "these items" : "this item"
                        }?`,
                    )}
                    cancelButtonText={translate("Cancel")}
                    submitButtonText={translate("Delete")}
                    onCancel={this.removeSelectedCancelHandler}
                    onSubmit={this.removeSelectedSubmitHandler}
                    submitTestSelector="submitDeleteListItems"
                ></TwoButtonModal>
                {/* New Print Functionality Removed as per ticket SURCOM-190 */}
                {/* {wishListLinesDataView.value && (
                    <WishListPrintModal
                        isOpen={this.state.printAllModalIsOpen}
                        handleClose={this.closePrintModal}
                        updateParameterFunction={(pageSize: number) => {
                            if (wishListLinesDataView.pagination?.pageSize === pageSize) {
                                return;
                            }

                            this.props.updateLoadWishListLinesParameter({ wishListId: wishList.id, pageSize });
                        }}
                        awaitableLoader={this.props.loadWishListLines}
                        initialPageSize={
                            wishListLinesDataView.value ? wishListLinesDataView.pagination?.pageSize || 8 : 8
                        }
                        reloading={false}
                        lineCollection={{
                            pagination: wishListLinesDataView.value ? wishListLinesDataView?.pagination || null : null,
                        }}
                        styles={styles.printListModal}
                        selectedIds={this.props.selectedWishListLineIds}
                    />
                )} */}
                <PrintProjectView includePrices={this.state.printIncludePrices} />
                <PrintIncludePricesModal
                    isOpen={this.state.includePricesModalIsOpen}
                    handleClose={this.closeIncludePricesModal}
                    onConfirm={this.handleIncludePricesConfirm}
                />
                {wishListLinesDataView.value && (
                    <PrintAllPagesModal
                        isOpen={this.state.printAllModalIsOpen}
                        handleClose={this.closePrintModal}
                        updateParameterFunction={pageSize => {
                            this.props.updateLoadWishListLinesParameter({ wishListId: wishList.id, pageSize });
                        }}
                        awaitableLoader={this.props.loadWishListLines}
                        initialPageSize={
                            wishListLinesDataView.value ? wishListLinesDataView.pagination?.pageSize || 8 : 8
                        }
                        reloading={false}
                        lineCollection={{
                            pagination: wishListLinesDataView.value ? wishListLinesDataView?.pagination || null : null,
                        }}
                        styles={styles.printListModal}
                    />
                )}
                <Modal
                    headline={translate("Copy List")}
                    isOpen={this.state.copyListModalIsOpen}
                    handleClose={this.copyCancelHandler}
                    closeOnEsc={this.state.closeCopyListModalOnEsc}
                    {...styles.copyListModal}
                >
                    <MyListsDetailsCopyListForm
                        onCancel={this.copyCancelHandler}
                        onSubmit={this.copySubmitHandler}
                        enableCloseCopyListModalOnEsc={this.enableCloseCopyListModalOnEsc}
                        disableCloseCopyListModalOnEsc={this.disableCloseCopyListModalOnEsc}
                    ></MyListsDetailsCopyListForm>
                </Modal>
                <ScheduleReminderModal
                    wishList={wishList}
                    isOpen={this.state.scheduleReminderModalIsOpen}
                    handleClose={this.closeScheduleReminderModalHandler}
                    extendedStyles={styles.scheduleReminderModal}
                />
                <AddTagsModal isOpen={this.state.addTagsModalIsOpen} handleClose={this.closeAddTagsModalHandler} />
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(MyListsDetailsActions)),
    definition: {
        group: "My Lists Details",
        displayName: "Actions",
        allowedContexts: ["MyListsDetailsPage"],
        fieldDefinitions: [
            {
                name: "overflowButtonsTitle",
                displayName: "Overflow Buttons",
                editorTemplate: "LabelField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 0,
            },
            {
                name: "overflowButtonsHorizontalRule",
                defaultValue: "",
                editorTemplate: "HorizontalRule",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.copyButton,
                displayName: "Copy",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
                sortOrder: 2,
            },
            {
                name: fields.exportButton,
                displayName: "Export",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
                sortOrder: 3,
            },
            {
                name: fields.printButton,
                displayName: "Print",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
                sortOrder: 4,
            },
            {
                name: fields.deleteListButton,
                displayName: "Delete List",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
                sortOrder: 5,
            },
            {
                name: fields.addTagsButton,
                displayName: "Add Tags",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
                sortOrder: 6,
            },
            {
                name: fields.editReminderButton,
                displayName: "Edit Reminder",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
                sortOrder: 7,
            },
            {
                name: fields.editListButton,
                displayName: "Edit List",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
                sortOrder: 8,
            },
            {
                name: fields.addListToCartButton,
                displayName: "Add List To Cart",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
                sortOrder: 9,
            },
            {
                name: fields.hideButton,
                displayName: "Hide",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
                sortOrder: 10,
            },
            {
                name: fields.shareButton,
                displayName: "Share",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
                sortOrder: 11,
            },
            {
                name: fields.removeSelectedButton,
                displayName: "Remove Selected",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
                sortOrder: 12,
            },
        ],
    },
};

export default widgetModule;
